import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useWizard } from 'react-use-wizard';

import { selectClient } from '@client/model/selectors';
import { analytics } from '@shared';

import {
  CcrcTypeAProductOptions,
  computeCcrcPricing,
  useCcrcTypeAProducts,
  type CcrcCoverageType,
} from '@/features/ccrc-type-a';
import { useAddPolicyFromQuote } from '@/features/guided-financial-planning/hooks';
import { StepLayout } from '@/features/guided-financial-planning/components/step-layout';

import { QuotesLoadingState, QuotesErrorState } from '../quotes-ui-states';

export function CcrcTypeASelectionStep() {
  const { nextStep } = useWizard();
  const client = useSelector(selectClient);

  const { products, isLoading, isError } = useCcrcTypeAProducts();
  const { selectedQuote, addCcrcTypeAProduct } = useAddPolicyFromQuote();

  const partner = client?.mutableClientPartner ?? null;
  const hasProducts = products.length > 0;

  // Track page view on mount
  useEffect(() => {
    if (client) {
      analytics.ccrc.typeASelectionViewed({
        clientId: client.clientId,
        ccrcType: 'type-a',
        stepId: 'ccrc-type-a-selection',
      });
    }
  }, [client?.clientId]);

  const handleSelect = (product: CcrcTypeAProduct, coverage: CcrcCoverageType) => {
    if (!client) {
      return;
    }

    const pricing = computeCcrcPricing({
      product,
      coverage,
      clientAge: client.intakeSurvey.clientAge,
      partnerAge: partner?.intakeSurvey.clientAge,
      hasPartner: partner != null,
    });

    // Track product selection
    analytics.ccrc.productSelected({
      clientId: client.clientId,
      ccrcType: 'type-a',
      productId: product.id,
      productName: product.productName,
      monthlyFee: pricing.totalMonthlyFee,
      entryFee: pricing.totalEntranceFee,
    });

    addCcrcTypeAProduct({ product, coverage, pricing });
    nextStep();
  };

  return (
    <StepLayout
      title="Your CCRC Options"
      subtitle="Here are your available CCRC Type A plans. Pick one to see how it fits your long-term care plan."
      onNext={() => nextStep()}
      isNextDisabled={!selectedQuote?.ccrcTypeAProduct}
    >
      {isLoading && <QuotesLoadingState />}

      {!isLoading && (isError || !hasProducts) && <QuotesErrorState />}

      {!isLoading && hasProducts && client && (
        <CcrcTypeAProductOptions
          products={products}
          client={client}
          partner={partner}
          onSelect={handleSelect}
        />
      )}
    </StepLayout>
  );
}
